import React, { useContext, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import { List } from 'immutable';

import { Table, TableRow, TableCell, TableBody } from '@comparethemarketau/manor-table';
import { Container } from '@comparethemarketau/manor-grid';

import { getProductId } from './comparing_products_intro';
import { ProductProviderLogo } from '../common/product_provider_logo';
import { ProductIntroCardPrice } from '../common/product_intro_card_price';
import { TableSectionHeader } from '../common/table_section_header';
import { useWindowScrollPosition } from '../common/use_window_scroll_position';

const useStyles = createUseStyles((theme) => ({
    comparingProductsStickiesContainer: {
        position: 'fixed',
        background: theme.colors.white,
        top: ({ stickyComponentTop }) => parseInt(stickyComponentTop, 10),
        left: 0,
        width: '100%',
        zIndex: 20
    },
    productsContainer: {
        width: ({ parentWidth }) => parentWidth
    },
    cell: {
        padding: 0
    },
    defaultCell: {
        background: theme.colors.white,
        borderColor: theme.colors.grey300,
        borderStyle: 'solid',
        borderWidth: '0 0.1rem 0 0'
    },
    bottomCell: {
        extend: 'defaultCell',
        paddingBottom: '2rem'
    },
    providerLogo: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '1rem'
    },
    tableSectionHeader: {
        boxShadow: theme.boxShadow.md
    }
}));

const ComparingProductsStickies = (props) => {
    const { selectedProducts, cellClassName, stackDisplayMode } = props;

    const [stickyStatus, setStickyStatus] = useState(StickStatus.HIDDEN);
    const ref = useRef(null);
    const [parentWidth, setParentWidth] = useState('100%');
    const scrollPosition = useWindowScrollPosition();

    const [stickyComponentTop, setStickyComponentTop] = useState(0);
    const [stickyComponentHeight, setStickyComponentHeight] = useState(0);
    const [firstTop, setFirstTop] = useState();
    const [secondTop, setSecondTop] = useState();

    const init = () => {
        if (ref && ref.current) {
            setStickyComponentTop(0 - ref.current.offsetHeight);
            setStickyComponentHeight(ref.current.offsetHeight);
        }
        const parentNode = document.getElementById(ParentNodeId);
        if (parentNode) {
            setParentWidth(`${parentNode.offsetWidth}px`);
        }
        const scrollTop = document.documentElement.scrollTop || 0;
        const trigger1 = document.getElementById(StickyTriggerPointId.FIRST);
        if (trigger1 && trigger1.getBoundingClientRect()) {
            setFirstTop(trigger1.getBoundingClientRect().top + scrollTop);
        }
        const trigger2 = document.getElementById(StickyTriggerPointId.SECOND);
        if (trigger2 && trigger2.getBoundingClientRect()) {
            setSecondTop(trigger2.getBoundingClientRect().top + scrollTop);
        }
    };

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (ref && ref.current && stickyComponentHeight !== ref.current.offsetHeight && stickyStatus === StickStatus.HIDDEN) {
            init();
        }
    });

    useEffect(() => {
        if (!firstTop || !secondTop) {
            return;
        }
        if (scrollPosition < firstTop) {
            setStickyStatus(StickStatus.HIDDEN);
            setStickyComponentTop(0 - stickyComponentHeight);
        } else if (scrollPosition >= firstTop) {
            setStickyStatus(StickStatus.FIRST_STICKY);
            const extend = scrollPosition - firstTop - stickyComponentHeight;
            if (extend <= 0) {
                setStickyComponentTop(extend);
            } else {
                setStickyComponentTop(0);
            }
        }
        const triggerOffsetDown = 30;
        if (scrollPosition >= triggerOffsetDown + secondTop - stickyComponentHeight) {
            setStickyStatus(StickStatus.SECOND_STICKY);
        }
    }, [scrollPosition]);

    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme, parentWidth, stickyComponentTop });

    return (
        <div className={classNames(classes.comparingProductsStickiesContainer)} ref={ref}>
            <Container className={classes.productsContainer}>
                <Table ariaLabel="Comparing products sticky table header" ariaDescribedby="product-sticky-table-header">
                    <TableBody>
                        <TableRow>
                            {
                                !stackDisplayMode && <TableCell className={classNames(classes.cell, cellClassName)} key="ComparingProductsStickies-spacer" />
                            }
                            {
                                selectedProducts.map((product) => {
                                    return (
                                        <TableCell align="center"
                                            className={classNames(classes.defaultCell, classes.cell, cellClassName)}
                                            key={`ProductProviderLogo-${getProductId(product)}`}
                                        >
                                            <div className={classes.providerLogo}>
                                                <ProductProviderLogo quote={product} />
                                            </div>
                                        </TableCell>
                                    );
                                })
                            }
                        </TableRow>
                        <TableRow>
                            {
                                !stackDisplayMode && <TableCell className={classNames(classes.cell, cellClassName)} key="ProductIntroCardPrice-spacer" />
                            }
                            {
                                selectedProducts.map((product) => {
                                    return (
                                        <TableCell align="center"
                                            className={classNames(classes.bottomCell, classes.cell, cellClassName)}
                                            key={`ProductIntroCardPrice-${getProductId(product)}`}
                                        >
                                            <ProductIntroCardPrice quote={product} />
                                        </TableCell>
                                    );
                                })
                            }
                        </TableRow>
                    </TableBody>
                </Table>
            </Container>
            <TableSectionHeader className={classes.tableSectionHeader} isKeyInformation={stickyStatus === StickStatus.FIRST_STICKY} />
        </div>
    );
};

ComparingProductsStickies.propTypes = {
    selectedProducts: PropTypes.instanceOf(List),
    cellClassName: PropTypes.string,
    stackDisplayMode: PropTypes.bool
};

const StickStatus = {
    HIDDEN: 0,
    FIRST_STICKY: 1,
    SECOND_STICKY: 2
};

const ParentNodeId = 'comparing-sticky-parent';

const StickyTriggerPointId = {
    FIRST: 'comparing-sticky-trigger-1',
    SECOND: 'comparing-sticky-trigger-2'
};

export { ComparingProductsStickies, StickStatus, ParentNodeId, StickyTriggerPointId };
